<template>
  <div class="shift-container">
    <h2>POS Shift Management</h2>

    <div v-if="!activeShift" class="card open-shift-card">
      <h3>No Active Shift</h3>
      <p>You must open a business shift before processing transactions.</p>
      
      <div class="form-group">
        <label for="username">Cashier Username:</label>
        <input 
          id="username" 
          v-model="username" 
          type="text" 
          placeholder="Enter cashier username" 
          :disabled="loading" 
        />
      </div>

      <button 
        @click="openShift" 
        :disabled="loading || !username.trim()" 
        class="btn btn-success"
      >
        {{ loading ? 'Opening...' : 'Open New Shift' }}
      </button>
    </div>

    <div v-else class="shift-dashboard">
      <div class="card status-card">
        <h3>Shift Active</h3>
        <p><strong>Shift ID:</strong> #{{ activeShift.shift_id }}</p>
        <p><strong>Business Date:</strong> {{ activeShift.business_date_start || activeShift.business_date }}</p>
        <p><strong>Opened At:</strong> {{ formatTime(activeShift.opened_at) }}</p>
        <p><strong>Cashier:</strong> {{ activeShift.username }}</p>

        <div v-if="closebalance.length" class="balance-section">
          <h4>Current Balances:</h4>
          <ul>
            <li v-for="(x, index) in closebalance" :key="index">
              <strong>{{ x.payment }}:</strong> {{ x.totalamount }}
            </li>
          </ul>
        </div>
        
        <button @click="closeShift" :disabled="loading" class="btn btn-danger">
          {{ loading ? 'Closing...' : 'Close Shift & Generate Report' }}
        </button>
      </div>

      <!-- <div class="card transaction-card">
        <h3>Process New Sale</h3>
        <div class="form-group">
          <label for="amount">Sale Amount ($):</label>
          <input 
            id="amount" 
            v-model.number="transactionAmount" 
            type="number" 
            min="0.01" 
            step="0.01" 
            placeholder="0.00" 
          />
        </div>
        <button 
          @click="submitTransaction" 
          :disabled="loading || !transactionAmount" 
          class="btn btn-primary"
        >
          Submit Transaction
        </button>
      </div> -->
    </div>

    <div v-if="shiftReport" class="card report-card">
      <h3>Shift Report Summary</h3>
      <hr />
      <p><strong>Business Date:</strong> {{ shiftReport.shift?.business_date_start }}</p>
      <p><strong>Duration:</strong> {{ formatTime(shiftReport.shift?.opened_at) }} to {{ formatTime(shiftReport.shift?.closed_at) }}</p>
      <p class="total-revenue"><strong>Total Revenue:</strong> ${{ shiftReport.totalAmount }}</p>
      
      <div v-if="shiftReport.entries?.length">
        <h4>Journal Entries Captured (Including Post-Midnight):</h4>
        <ul>
          <li v-for="entry in shiftReport.entries" :key="entry.id">
            {{ formatTime(entry.created_at) }} - ${{ entry.amount }}
          </li>
        </ul>
      </div>
      <button @click="clearReport" class="btn btn-secondary">Done</button>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'ShiftManager',

  // Reactivity State
  data() {
    return {
      username: 'casher1',
      activeShift: null,       
      transactionAmount: null, 
      shiftReport: null,       
      loading: false,          
      closebalance: []         
    };
  },

  // Lifecycle Hook
  async mounted() {
    await this.checkActiveShift();
  },

  // Methods Mode Logic
  methods: {
    // Utility to clean up response objects
    unwrapData(response) {
      return response.data?.data ? response.data.data : response.data;
    },

    // Formats timestamps elegantly
    formatTime(isoString) {
      if (!isoString) return 'N/A';
      const date = new Date(isoString);
      return isNaN(date.getTime()) 
        ? isoString 
        : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },

    // Dynamic Balance Fetcher accepting shift_id parameter
    async fetchClosingShiftBalance(shiftId) {
      try {
        const response = await axios.get('/pos/getclosingshift', {
          params: { shift_id: shiftId }
        });
        return this.unwrapData(response) || [];
      } catch (error) {
        console.error('Error fetching closing balance data:', error);
        return [];
      }
    },

    // 1. App Startup Check
    async checkActiveShift() {
      this.loading = true;
      try {
        const shiftRes = await axios.get('/pos/getactiveshift');
        const shiftData = this.unwrapData(shiftRes);

        let foundShift = null;
        if (Array.isArray(shiftData) && shiftData.length > 0) {
          foundShift = shiftData[0];
        } else if (shiftData && !Array.isArray(shiftData)) {
          foundShift = shiftData;
        }

        if (foundShift?.id) {
          this.activeShift = foundShift;
          this.closebalance = await this.fetchClosingShiftBalance(foundShift.shift_id);
        } else {
          this.activeShift = null;
          this.closebalance = [];
        }
      } catch (error) {
        console.error('Error checking active shift status:', error);
      } finally {
        this.loading = false;
      }
    },

    // 2. Open Shift Action
    async openShift() {
      if (!this.username.trim()) return;
      this.loading = true;
      try {
        const response = await axios.post('/pos/createshift', { username: this.username });
        const resData = this.unwrapData(response);
        
        const targetShift = Array.isArray(resData) ? resData[0] : resData;
        this.activeShift = targetShift;
        this.shiftReport = null; 
        
        if (targetShift?.id) {
          this.closebalance = await this.fetchClosingShiftBalance(targetShift.shift_id);
        }
      } catch (error) {
        alert('Failed to open shift: ' + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    // 3. Complete Sale Transaction
    async submitTransaction() {
      if (!this.activeShift || !this.transactionAmount) return;
      
      this.loading = true;
      try {
        const payload = {
          shift_id: this.activeShift.id,
          amount: this.transactionAmount,
          details: 'POS Sale'
        };

        await axios.post('/api/transactions', payload);
        alert('Transaction successful!');
        this.transactionAmount = null;
        
        // Refresh balance parameters for this exact active shift ID
        this.closebalance = await this.fetchClosingShiftBalance(this.activeShift.id);
      } catch (error) {
        alert('Transaction failed: ' + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    // 4. Close Shift Action
    async closeShift() {
      if (!confirm('Are you sure you want to close this shift and lock transactions?')) return;
      
      this.loading = true;
      try {
        const response = await axios.put(`/pos/closingshift/${this.activeShift.shift_id}`);
        this.shiftReport = this.unwrapData(response.data.data[0]); 
        this.activeShift = null;    
        this.closebalance = [];
      } catch (error) {
        alert('Failed to close shift: ' + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    clearReport() {
      this.shiftReport = null;
    }
  }
};
</script>

<style scoped>
.shift-container {
  max-width: 600px;
  margin: 2rem auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 0 1rem;
}
.card {
  background: #fdfdfd;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);
}
.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}
.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
}
input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
}
input:focus {
  border-color: #3498db;
  outline: none;
}
.balance-section {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  margin: 1rem 0;
}
.balance-section h4 { margin: 0 0 0.5rem 0; }
.balance-section ul { margin: 0; padding-left: 1.25rem; }
.btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  transition: background 0.2s ease;
}
.btn-success { background: #2ecc71; color: white; }
.btn-success:hover { background: #27ae60; }
.btn-danger { background: #e74c3c; color: white; }
.btn-danger:hover { background: #c0392b; }
.btn-primary { background: #3498db; color: white; margin-top: 0.5rem; }
.btn-primary:hover { background: #2980b9; }
.btn-secondary { background: #95a5a6; color: white; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.total-revenue { font-size: 1.3rem; color: #2ecc71; font-weight: bold; }
</style>